/**
 * Auth token revocation and reconnect verification (R39 A12.a, SO-P9, SO-P10).
 *
 * On app start while online, and on every offline -> online change, forces a token refresh.
 * On auth/user-token-expired, auth/user-disabled or auth/invalid-user-token:
 * runs full local wipe through logout, clearing:
 * - Firestore cache (IndexedDB)
 * - helper and member outboxes
 * - count drafts
 * - offline-expiry stamp
 * - session user
 * Displays sign-in message (NLS).
 * Network errors are not revocation: nothing is wiped.
 * Shared-device routes keep their own revoke path (SharedDeviceShell untouched).
 */

import { recordServerContact, clearOfflineExpiryStamp, clearFirestoreIndexedDatabases } from './offlineExpiry'
import { clearAllCountDrafts } from './helper/helperCountDraftStore'
import { clearAllHelperOutbox, clearHelperOutboxForUser } from './helper/helperOutboxDb'
import { clearAllMemberOutbox, clearMemberOutboxForUser } from './memberOutboxService'
import { isSharedDevicePath } from '../utils/sharedDeviceRoute'

export const REVOCATION_ERROR_CODES = [
  'auth/user-token-expired',
  'auth/user-disabled',
  'auth/invalid-user-token',
]

export function isRevocationError(error: unknown): boolean {
  if (!error) return false
  const code = String((error as { code?: string }).code ?? '')
  const message = String((error as { message?: string }).message ?? '')
  return (
    REVOCATION_ERROR_CODES.includes(code) ||
    REVOCATION_ERROR_CODES.some((c) => message.includes(c))
  )
}

export function isNetworkError(error: unknown): boolean {
  if (!error) return false
  const rawCode = String((error as { code?: string }).code ?? '')
  const code = rawCode.startsWith('functions/') ? rawCode.slice('functions/'.length) : rawCode
  const message = error instanceof Error
    ? error.message
    : String((error as { message?: string })?.message ?? (typeof error === 'string' ? error : ''))
  const lowerMsg = message.toLowerCase()
  return (
    code === 'auth/network-request-failed' ||
    code === 'unavailable' ||
    code === 'deadline-exceeded' ||
    message.includes('network-request-failed') ||
    lowerMsg.includes('network error') ||
    lowerMsg.includes('failed to fetch') ||
    lowerMsg.includes('unavailable') ||
    lowerMsg.includes('load failed') ||
    lowerMsg.includes('timeout')
  )
}

export interface RefreshableUser {
  getIdToken: (forceRefresh?: boolean) => Promise<string>
  uid: string
}

export interface VerifyRevocationOptions {
  isSharedDevice?: boolean
  onRevoked?: (reasonCode: string) => Promise<void>
  onSuccess?: () => void
}

export type VerifyRevocationResult =
  | { status: 'VALID' }
  | { status: 'REVOKED'; code: string }
  | { status: 'NETWORK_ERROR' }
  | { status: 'SKIPPED'; reason: string }

/**
 * Checks token validity with forceRefresh = true.
 * If valid: records server contact (proof of reconnect).
 * If revoked: triggers revocation callback.
 * If network error: ignores without wiping.
 */
export async function verifyUserTokenRevocation(
  user: RefreshableUser | null,
  options: VerifyRevocationOptions = {},
): Promise<VerifyRevocationResult> {
  if (!user) {
    return { status: 'SKIPPED', reason: 'NO_USER' }
  }

  const pathname = typeof window !== 'undefined' ? window.location.pathname : ''
  const isShared = options.isSharedDevice ?? isSharedDevicePath(pathname)
  if (isShared) {
    return { status: 'SKIPPED', reason: 'SHARED_DEVICE_ROUTE' }
  }

  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return { status: 'SKIPPED', reason: 'OFFLINE' }
  }

  try {
    await user.getIdToken(true)
    recordServerContact()
    options.onSuccess?.()
    return { status: 'VALID' }
  } catch (err: unknown) {
    if (isRevocationError(err)) {
      const code = (err as { code?: string }).code || 'auth/user-token-expired'
      if (options.onRevoked) {
        await options.onRevoked(code)
      }
      return { status: 'REVOKED', code }
    }
    if (isNetworkError(err)) {
      return { status: 'NETWORK_ERROR' }
    }
    return { status: 'SKIPPED', reason: 'OTHER_ERROR' }
  }
}

/**
 * Executes a full local wipe across all client storage tiers:
 * - count drafts
 * - offline-expiry stamp
 * - helper & member outboxes
 * - Firestore IndexedDB cache
 */
export async function runFullLocalWipe(uid?: string | null): Promise<void> {
  // 1. Count drafts
  clearAllCountDrafts()

  // 2. Offline expiry stamp & workspace limits
  clearOfflineExpiryStamp()

  // 3. Outboxes
  if (uid) {
    await Promise.all([
      clearHelperOutboxForUser(uid).catch(() => {}),
      clearMemberOutboxForUser(uid).catch(() => {}),
    ])
  }
  await Promise.all([
    clearAllHelperOutbox().catch(() => {}),
    clearAllMemberOutbox().catch(() => {}),
  ])

  // 4. Firestore persistent cache
  await clearFirestoreIndexedDatabases()
}
